<template>
  <v-card class="pa-2 mb-4">
    <v-toolbar class="text-h5">
      <v-layout align-center justify-space-between>
        <v-flex>Customer Details</v-flex>
        <update-info :tenant="tenant" @success="updateSuccess" />
      </v-layout>
    </v-toolbar>
    <v-container>
      <v-layout class="subheading" align-start>
        <v-layout column>
          <v-layout class="my-1">
            <v-flex xs4>Name:</v-flex>
            <v-flex xs6 data-testid="tenant-name">{{ tenant.first_name }} {{ tenant.last_name }}</v-flex>
          </v-layout>
          <v-layout class="my-1">
            <v-flex xs4>E-mail:</v-flex>
            <v-flex xs6 data-testid="tenant-email">{{ tenant.email }}</v-flex>
          </v-layout>
          <v-layout class="my-1">
            <v-flex xs4>Address:</v-flex>
            <v-flex xs6 data-testid="tenant-address">
              <span v-if="tenant.street_address">{{ tenant.street_address }}</span>
              <span v-else>None on file</span>
              <div v-if="tenant.street_address && tenant.street_address_2"> {{ tenant.street_address_2 }}</div>
            </v-flex>
          </v-layout>
          <v-layout
            v-if="tenant.street_address"
            class="my-1"
          >
            <v-flex xs4 />
            <v-flex xs6 data-testid="tenant-city-state-zip">
              {{ tenant.city }}, {{ tenant.state }} {{ tenant.postal_code }}
            </v-flex>
          </v-layout>
          <v-layout class="my-1">
            <v-flex xs4>Phone:</v-flex>
            <v-flex xs6 data-testid="tenant-phone">{{ addDashes(tenant.phone_number) }}</v-flex>
          </v-layout>
        </v-layout>
        <v-layout column>
          <v-layout class="my-1">
            <v-flex xs4>Facility:</v-flex>
            <v-flex xs6 data-testid="location-code">
              <span v-if="facility && facility.location_code">{{ facility.location_code }}</span>
              <span v-else>LTRAIN</span>
            </v-flex>
          </v-layout>
          <v-layout class="my-1">
            <v-flex xs4>Tenant ID:</v-flex>
            <v-flex xs6 data-testid="tenant-id">{{ tenant.external_id }}</v-flex>
          </v-layout>
          <v-layout class="my-1">
            <v-flex xs4>Access Code:</v-flex>
            <v-flex xs6 data-testid="tenant-gate-code">{{ tenant.access_code }}</v-flex>
          </v-layout>
          <v-layout class="my-1">
            <v-flex xs4 v-if="parseInt(tenant.id_type, 10) === 2">State ID:</v-flex>
            <v-flex xs4 v-else-if="parseInt(tenant.id_type, 10) === 3">Passport Number:</v-flex>
            <v-flex xs4 v-else>Driver's License:</v-flex>
            <v-flex xs6 data-testid="tenant-id-number">
              <span v-if="tenant.id_number">{{ tenant.id_number }}</span>
              <span v-else>None on file</span>
            </v-flex>
          </v-layout>
          <v-layout class="my-1" v-if="parseInt(tenant.id_type, 10) !== 3">
            <v-flex xs4 >State:</v-flex>
            <v-flex xs6 data-testid="tenant-id-state">
              <span v-if="tenant.id_number">{{ tenant.id_state }}</span>
              <span v-else>None on file</span>
            </v-flex>
          </v-layout>
          <!-- Disabled as of Feb 27, 2024.  Leaving the code because this might be re-enabled later.
          <v-layout class="my-1">
            <v-flex xs4>SMS Consent:</v-flex>
            <v-flex xs6 data-testid="tenant-sms-opt-in">
              <v-checkbox
                v-show="!$store.state.smsOptIn.loading"
                dense
                readonly
                class="ma-0 pa-0"
                v-model="smsOptInValue"
                @click="showSmsOptInConfirmation = true;" />
              <v-progress-circular
                v-show="$store.state.smsOptIn.loading"
                indeterminate
                :size="20"
                :width="2" />
              <v-dialog v-model="showSmsOptInConfirmation" width="650">
                <v-card>
                  <v-card-title class="text-h5" primary-title>
                    SMS Consent Confirmation
                  </v-card-title>
                  <v-card-text>
                    <span class v-if="smsOptInValue">
                      Are you sure you want to remove phone number {{ tenant.phone_number }}
                      from the SMS consent list?
                    </span>
                    <span class v-else>
                      Are you sure you want to add phone number {{ tenant.phone_number }}
                      to the SMS consent list?
                    </span>
                  </v-card-text>
                  <v-card-actions class="text-center">
                    <v-btn @click="showSmsOptInConfirmation = false" color="error" elevation="0">Cancel</v-btn>
                    <v-spacer />
                    <v-btn @click="updateSmsOptIn(!smsOptInValue)" color="primary" elevation="0">Confirm</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-flex>
          </v-layout>
          -->
          <v-layout class="my-1" v-if="isBusinessCustomer">
            <v-flex xs4>Business Customer:</v-flex>
            <v-flex xs6 data-testid="tenant-business-customer">Yes</v-flex>
          </v-layout>
          <v-layout class="my-1" v-if="isCompanyName">
            <v-flex xs4>Company Name:</v-flex>
            <v-flex xs6 data-testid="tenant-company-name">{{ companyName }}</v-flex>
          </v-layout>
        </v-layout>
      </v-layout>
    </v-container>
  </v-card>
</template>

<script>
import UpdateInfo from "@/components/UpdateInfo";
import { debounce } from "lodash";

export default {
  name: "TenantDetails",

  data() { return {
    showSmsOptInConfirmation: false
  }},

  props: {
    facility: {
      type: [Object, undefined],
      default: undefined
    }
  },

  methods: {
    updateSuccess() {
      this.$emit("updateSuccess");
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    updateSmsOptIn(value) {
      this.showSmsOptInConfirmation = false;
      this.$store.state.smsOptIn.loading = true;
      debounce(() => {
        this.$store.dispatch("smsOptIn/update", {
          phone_number: this.tenant.phone_number,
          value
        });
      }, 500)();
    }
  },

  computed: {
    tenant() {
      return this.$store.state.tenant.info;
    },

    companyTenant() {
      return this.$store.state.tenant.companyInfo;
    },

    isBusinessCustomer() {
      if (this.companyTenant && this.companyTenant.bCompanyIsTenant) {
        return true;
      }

      return !!(this.tenant && this.tenant.is_business);
    },

    isCompanyName() {
      if (this.companyTenant && this.companyTenant.business_name) {
        return true;
      }

      return !!(this.tenant && this.tenant.is_business);
    },

    companyName() {
      return this.tenant.business_name ? this.tenant.business_name : this.companyTenant.business_name;
    },

    smsOptInValue: {
      get() {
        return this.$store.state.smsOptIn.smsOptIn.value;
      },
      set(value) {
        this.$store.state.smsOptIn.smsOptIn.value = value;
      }
    }
  },

  components: {
    UpdateInfo
  }
};
</script>
